/**
 * MentionPanel — the conversation's members, listed above the composer when the person types "@"
 * (conversation-members spec). Demo page: /chats (the strip above the composer); built like the slash-command panel
 * (`ConversationCommandPanel.tsx`), whose result rows it copies.
 *
 * It renders inside the thread's composer, so it reads the typed text from the composer itself; picking a member writes
 * "@<name> " in place of the "@" being typed and adds the member to who the message is for (`onPick`), by its address.
 */
import { useAui, useAuiState } from '@assistant-ui/react';
import { type ConversationMemberRef, memberKindLabel } from '@confluent/shared/lib/conversation-addressing';
import { Bot, UserRound } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '../ui/Button.js';
import { insertMention, matchingMembers, mentionQuery } from './conversation-mentions.js';
import type { MentionKeys } from './useMentionKeys.js';

interface MentionPanelProps {
  /** The members the person can address (never the person themselves). */
  members: readonly ConversationMemberRef[];
  keys: MentionKeys;
  onPick: (member: ConversationMemberRef) => void;
}

export function MentionPanel({ members, keys, onPick }: MentionPanelProps) {
  const text = useAuiState((s) => s.composer.text);
  const aui = useAui();
  const typed = mentionQuery(text);
  const open = typed !== null && typed.start !== keys.dismissedAt;
  const matches = open ? matchingMembers(members, typed.query) : [];

  // The keys move through what is listed: tell them whenever the list changes.
  const listed = matches.map((member) => member.id).join(',');
  const { onOptionsChange, resetHighlight } = keys;
  // `listed` names the list; `matches` is a new array on every render.
  useEffect(() => {
    onOptionsChange(matches);
  }, [listed, onOptionsChange]);
  const query = typed?.query;
  useEffect(() => {
    if (query !== undefined) resetHighlight();
  }, [query, resetHighlight]);

  const pick = (member: ConversationMemberRef) => {
    if (!typed) return;
    aui.composer().setText(insertMention(text, typed.start, member));
    onPick(member);
  };
  const pickRef = keys.pickRef;
  useEffect(() => {
    pickRef.current = pick;
    return () => {
      pickRef.current = null;
    };
  });

  if (matches.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-card-border bg-surface" data-testid="mention-panel">
      <div className="border-b border-card-border bg-background-gray-secondary_alt px-2.5 py-1.5 text-[11px] text-text-tertiary">
        Address a member — only the members you name receive the message. ↑↓ to choose, Enter to pick, Esc to close.
      </div>
      <div className="max-h-[220px] overflow-y-auto" role="listbox" aria-label="Members of this conversation">
        {matches.map((member, index) => {
          const highlighted = index === keys.highlightIndex;
          const Icon = member.memberType === 'user' ? UserRound : Bot;
          return (
            <Button
              key={member.id}
              variant="ghost"
              role="option"
              aria-selected={highlighted}
              data-highlighted={highlighted || undefined}
              onClick={() => pick(member)}
              className={`h-auto w-full justify-start gap-2 rounded-none px-2.5 py-2 text-left font-normal ${
                highlighted ? 'bg-background-gray-secondary' : ''
              }`}
            >
              <Icon size={13} className="shrink-0 text-muted" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-title-50">{member.name}</span>
              <span className="shrink-0 text-[10px] text-muted">
                {member.role === 'lead'
                  ? `${memberKindLabel(member.memberType)} · lead`
                  : memberKindLabel(member.memberType)}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
